/**
 * Platform Admin Layout
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  Building2, 
  CreditCard, 
  Package, 
  Headphones, 
  Settings,
  LogOut
} from 'lucide-react';

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();

  const navItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/businesses', icon: Building2, label: 'Businesses' },
    { path: '/subscriptions', icon: CreditCard, label: 'Subscriptions' },
    { path: '/products', icon: Package, label: 'Products' },
    { path: '/support', icon: Headphones, label: 'Support' },
    { path: '/settings', icon: Settings, label: 'Settings' },
  ];

  return (
    <div style={styles.container}>
      {/* Sidebar */}
      <aside style={styles.sidebar}>
        <div style={styles.logo}>
          <span style={styles.logoIcon}>🥭</span>
          <div>
            <div style={styles.logoText}>Mangoes Unified</div>
            <div style={styles.logoSubtext}>Platform Admin</div>
          </div>
        </div>

        <nav style={styles.nav}>
          {navItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              style={{
                ...styles.navItem,
                ...(location.pathname === item.path ? styles.navItemActive : {})
              }}
            >
              <item.icon size={20} />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        <div style={styles.userSection}>
          <div style={styles.userInfo}>
            <div style={styles.avatar}>
              {user?.firstName?.charAt(0) || 'Z'}
            </div>
            <div>
              <div style={styles.userName}>{user?.firstName} {user?.lastName}</div>
              <div style={styles.userEmail}>{user?.email}</div>
            </div>
          </div>
          <button onClick={logout} style={styles.logoutBtn}>
            <LogOut size={18} />
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main style={styles.main}>
        {children}
      </main>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    minHeight: '100vh',
  },
  sidebar: {
    width: '280px',
    background: '#1a1a2e',
    borderRight: '1px solid #252540',
    display: 'flex',
    flexDirection: 'column',
    padding: '20px',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '40px',
  },
  logoIcon: {
    fontSize: '2rem',
  },
  logoText: {
    fontSize: '1.2rem',
    fontWeight: 700,
    color: '#FFA500',
  },
  logoSubtext: {
    fontSize: '0.75rem',
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  nav: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    borderRadius: '8px',
    color: '#9ca3af',
    textDecoration: 'none',
    transition: 'all 0.2s',
  },
  navItemActive: {
    background: 'rgba(255, 165, 0, 0.1)',
    color: '#FFA500',
  },
  userSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    borderTop: '1px solid #252540',
    marginTop: 'auto',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  avatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: '#FFA500',
    color: '#1a1a2e',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
  },
  userName: {
    fontWeight: 500,
  },
  userEmail: {
    fontSize: '0.8rem',
    color: '#9ca3af',
  },
  logoutBtn: {
    background: 'none',
    border: 'none',
    color: '#9ca3af',
    cursor: 'pointer',
    padding: '8px',
  },
  main: {
    flex: 1,
    padding: '30px',
    overflow: 'auto',
    background: '#0f0f1a',
  },
};

export default Layout;
